import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Typography,
} from "@material-ui/core";
import { Theme, makeStyles } from "@material-ui/core/styles";
import { Clear, Done, MoreVert, Share } from "@material-ui/icons";
import { App, Events, Workouts } from "app-interfaces";
import { TFunction } from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import Popup from "reactjs-popup";
import { v4 as uuid } from "uuid";

import { useStateValue } from "../../config/AppContext";
import { Action, TemplateProps } from "../../types/StaticTypes";
import SessionAdd from "../Sessions/SessionAdd";
import SessionAvailability from "../Sessions/SessionAvailability";
import SessionCancel from "../Sessions/SessionCancel";
import SessionDetails from "../Sessions/SessionDetails/SessionDetails";
import SessionEdit from "../Sessions/SessionEdit";
import SessionShare from "../Sessions/SessionShare";
import BackArrow from "../Utilities/BackArrow";
import { hasHappenedBeforeThisInstant } from "../Utilities/CustomLuxonUtils";
import { AddWorkoutWithRouter } from "../Workouts/Add";
import WorkoutsListings from "../Workouts/WorkoutsListings";

function noop() {}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexGrow: 1,
  },
  dialog: {
    "& .MuiDialog-paper": {
      position: "absolute",
      width: "100%",
      margin: 0,
      top: 0,
      bottom: 0,
    },
    "& .MuiDialogTitle-root": {
      height: 75,
      backgroundColor: "var(--text-primary)",
      color: "#ffffff",
      fontSize: 20,
      fontWeight: 500,
      padding: "25px 15px",
      "& svg": {
        marginRight: 15,
        marginTop: -2,
      },
      "& .session-options": {
        float: "right",
        fontSize: 14,
        fontWeight: 400,
        "& li": {
          fontSize: 14,
          fontWeight: "bold",
          padding: "5px 0",
          "& svg": {
            height: 24,
            width: 24,
            marginRight: 7,
          },
        },
      },
    },
    "& .MuiDialogContent-root": {
      overflow: "auto",
      height: "85vh",
    },
    "& .MuiDialogActions-root": {
      paddingLeft: 20,
      paddingRight: 20,
      width: "100%",
      "& button": {
        height: 50,
        backgroundColor: "var(--text-primary)",
        color: "#ffffff",
        fontSize: theme.spacing(2),
        textTransform: "none",
      },
    },
  },
  drawer: {
    "& .MuiDrawer-paper": {
      width: 512,
      "& .drawer-heading": {
        height: 75,
        backgroundColor: "var(--text-primary)",
        color: "#ffffff",
        fontSize: 20,
        fontWeight: 500,
        padding: "25px 15px",
        "& svg": {
          marginRight: 15,
          marginTop: -2,
        },
        "& .session-options": {
          float: "right",
          fontSize: 14,
          fontWeight: 400,
          "& li": {
            fontSize: 14,
            fontWeight: "bold",
            padding: "5px 0",
            "& svg": {
              height: 24,
              width: 24,
              marginRight: 7,
            },
          },
        },
      },
      "& .drawer-body": {
        overflow: "auto",
        height: "85vh",
      },
      "& .drawer-actions": {
        display: "flex",
        justifyContent: "space-evenly",
        "& .MuiDialogActions-root": {
          paddingLeft: 20,
          paddingRight: 20,
          width: "100%",
          "& button": {
            height: 50,
            backgroundColor: "var(--text-primary)",
            color: "#ffffff",
            fontSize: theme.spacing(2),
            textTransform: "none",
          },
        },
      },
    },
  },
}));

export enum SessionFormView {
  DEFAULT = "default",
  CANCEL = "cancel",
  SHARE = "share",
  RESCHEDULE = "reschedule",
  CONFIRM_RESCHEDULE = "confirm-reschedule",
  SCRATCH = "scratch",
  TEMPLATE = "template",
  EDIT_WORKOUT = "edit-workout",
}

type SessionFormProps = {
  children?: any;
  show: boolean;
  time?: { start: string; end: string; datetime: Date };
  event?: Events.IEvents;
  session?: Events.SessionFull | null;
  view: SessionFormView;
  setView: (view: SessionFormView) => void;
  dispatch: React.Dispatch<Action>;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onHide: () => void;
  handleWorkout?: (params: Workouts.IWorkout) => void;
};

/* Just a quick refactor to avoid duplication. Should be moved into a <Switch> using Router */
function getActionTitle(currentView: string, t: TFunction, hasSession: boolean) {
  return currentView === "reschedule" || currentView === "confirm-reschedule"
    ? t("Reschedule session")
    : currentView === "template"
    ? t("Select workout")
    : currentView === "scratch"
    ? t("Add workout")
    : currentView === "share"
    ? t("Share session")
    : currentView === "edit-workout"
    ? t("Edit Session Workout")
    : currentView === "cancel"
    ? t("Cancel session")
    : !hasSession
    ? t("Add Session")
    : t("Session details");
}

function renderDrawerActionForView(
  currentView: string,
  t: TFunction,
  session: Events.SessionFull,
  handleAdd: any,
  setCurrentViewFromTargetId: any
) {
  return currentView === "edit-workout" ? (
    <DialogActions>
      <Button fullWidth variant="contained" form="session-edit" type="submit">
        {t("Done")}
      </Button>
    </DialogActions>
  ) : currentView === "share" ? (
    <DialogActions>
      <Button
        fullWidth
        id="submit-share"
        variant="contained"
        form="session-share"
        type="submit"
        disabled
      >
        {t("Done")}
      </Button>
    </DialogActions>
  ) : currentView === "cancel" ? (
    <DialogActions>
      <Button fullWidth id="submit-cancel" form="session-cancel" variant="contained" type="submit">
        {t("Confirm")}
      </Button>
    </DialogActions>
  ) : !session ? (
    <DialogActions>
      <Button fullWidth variant="contained" color="primary" onClick={handleAdd}>
        {t("Save Session")}
      </Button>
    </DialogActions>
  ) : !session.workout &&
    currentView !== "reschedule" &&
    currentView !== "scratch" &&
    currentView !== "template" ? (
    <DialogActions>
      <Button fullWidth id="template" variant="contained" onClick={setCurrentViewFromTargetId}>
        {t("Select workout")}
      </Button>
      <Button id="scratch" variant="contained" onClick={setCurrentViewFromTargetId} fullWidth>
        {t("Start from scratch")}
      </Button>
    </DialogActions>
  ) : (
    <></>
  );
}

function DesktopView({
  classes,
  onHide,
  show,
  currentView,
  setCurrentView,
  session,
  actionTitle,
  renderPopupActions,
  renderSession,
  handleAdd,
  setCurrentViewFromTargetId,
}: any) {
  const { t } = useTranslation();

  const backArrowAction =
    currentView === SessionFormView.DEFAULT
      ? onHide
      : currentView === SessionFormView.CONFIRM_RESCHEDULE
      ? () => setCurrentView(SessionFormView.RESCHEDULE)
      : () => setCurrentView(SessionFormView.DEFAULT);

  const defaultCurrentViewPopup = (
    <>
      <span
        className="session-options"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Popup
          position="bottom right"
          mouseEnterDelay={0}
          repositionOnResize={true}
          trigger={<MoreVert />}
          contentStyle={{
            textAlign: "left",
            padding: 10,
            cursor: "pointer",
            borderRadius: 5,
            backgroundColor: "var(--white)",
            color: "var(--text-primary)",
            width: "240px",
          }}
        >
          {renderPopupActions}
        </Popup>
      </span>
    </>
  );

  return (
    <Drawer id="SessionForm" anchor="right" className={classes.drawer} open={show} onClose={onHide}>
      <Typography variant="h5" className="drawer-heading">
        <BackArrow show={true} variant="primary" action={backArrowAction} />
        {actionTitle}
        {currentView === "default" && session && defaultCurrentViewPopup}
      </Typography>
      <div className="drawer-body">{renderSession()}</div>
      <div className="drawer-actions">
        {renderDrawerActionForView(currentView, t, session, handleAdd, setCurrentViewFromTargetId)}
      </div>
    </Drawer>
  );
}

function MobileView({
  show,
  onHide,
  classes,
  currentView,
  setCurrentView,
  session,
  renderPopupActions,
  renderSession,
  setCurrentViewFromTargetId,
  handleAdd,
  actionTitle,
}: any) {
  const { t } = useTranslation();

  return (
    <Dialog id="SessionForm" open={show} onClose={onHide} className={classes.dialog}>
      <DialogTitle>
        <BackArrow
          show={true}
          variant="primary"
          action={
            currentView === "default"
              ? onHide
              : currentView === "confirm-reschedule"
              ? () => setCurrentView("reschedule")
              : () => setCurrentView("default")
          }
        />
        {actionTitle}
        {currentView === "default" && session && (
          <span
            className="session-options"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Popup
              position="bottom right"
              repositionOnResize={true}
              trigger={<MoreVert />}
              contentStyle={{
                textAlign: "left",
                cursor: "pointer",
                padding: 10,
                borderRadius: 5,
                backgroundColor: "#ffffff",
                color: "var(--text-primary)",
                width: "240px",
              }}
            >
              {renderPopupActions}
            </Popup>
          </span>
        )}
      </DialogTitle>
      <DialogContent
        style={{
          overflow: "auto",
          minHeight: "fit-content",
          maxHeight: "70vh",
        }}
      >
        {renderSession()}
      </DialogContent>
      {currentView === "edit-workout" ? (
        <DialogActions>
          <Button fullWidth variant="contained" form="session-edit" type="submit">
            {t("Done")}
          </Button>
        </DialogActions>
      ) : currentView === "share" ? (
        <DialogActions>
          <Button
            fullWidth
            id="submit-share"
            variant="contained"
            form="session-share"
            type="submit"
            disabled
          >
            {t("Done")}
          </Button>
        </DialogActions>
      ) : currentView === "cancel" ? (
        <DialogActions>
          <Button
            fullWidth
            id="submit-cancel"
            form="session-cancel"
            variant="contained"
            type="submit"
          >
            {t("Confirm")}
          </Button>
        </DialogActions>
      ) : !session ? (
        <DialogActions>
          <Button fullWidth variant="contained" color="primary" onClick={handleAdd}>
            {t("Save Session")}
          </Button>
        </DialogActions>
      ) : !session.workout &&
        currentView !== "reschedule" &&
        currentView !== "scratch" &&
        currentView !== "template" ? (
        <DialogActions>
          <Button fullWidth id="template" variant="contained" onClick={setCurrentViewFromTargetId}>
            {t("Select workout")}
          </Button>
          <Button
            fullWidth
            id="scratch"
            variant="contained"
            style={{ opacity: 0.9 }}
            onClick={setCurrentViewFromTargetId}
          >
            {t("Start from scratch")}
          </Button>
        </DialogActions>
      ) : (
        <></>
      )}
    </Dialog>
  );
}

export default function SessionFormModal(props: SessionFormProps): JSX.Element {
  const classes = useStyles();
  const { t } = useTranslation();
  const { metric } = useStateValue() as {
    metric: App.State["metric"];
  };
  const {
    show,
    time,
    event,
    session,
    view: currentView,
    setView: setCurrentView,
    dispatch,
    handleWorkout,
    onSubmit,
    onHide,
  } = props;

  const handleAdd = () => {
    const submitButton =
      (document.getElementById("session-submit") as HTMLButtonElement | null) ||
      (document.getElementById("submit") as HTMLButtonElement | null);
    if (!submitButton) return;
    return submitButton.click();
  };

  const setCurrentViewFromTargetId: React.MouseEventHandler<HTMLElement> = (e) => {
    const viewName = e.currentTarget.id;
    const validViewNames = Object.values(SessionFormView).map((v) => v.toString());

    if (!validViewNames.includes(viewName)) {
      throw new TypeError(
        `${viewName} is not a valid view name. Valid values: ${validViewNames.join(",")}`
      );
    }
    setCurrentView(viewName as SessionFormView);
  };

  const resetView = (start: Date | string) => {
    dispatch({ type: "dashboard", payload: { start } });
    return setCurrentView(SessionFormView.DEFAULT);
  };

  const saveWorkout = (workout: Workouts.IWorkout) => {
    if (handleWorkout) handleWorkout(workout);
    return setCurrentView(SessionFormView.DEFAULT);
  };

  const updateWorkout = (params: TemplateProps) => {
    return saveWorkout(params.workout);
  };

  const createWorkout = (params: TemplateProps) => {
    const {
      workout: { id, name, description, duration, exercise_super_set, notes },
    } = params;

    const newWorkout = {
      id,
      name,
      description,
      duration,
      exercise_super_set: exercise_super_set?.map((superset) => ({
        ...superset,
        id: uuid(),
        exercises: superset.exercises?.map((exercise) => ({
          ...exercise,
          id: uuid(),
          result_vals: [],
        })),
      })),
      notes,
      is_deleted: false,
    };

    return saveWorkout(newWorkout);
  };
  const shareSession = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!event) return;
    const formData = new FormData(e.currentTarget);
    const params = {
      id: event.id,
      to_customer: formData.get("to_customer") === "true",
      to_self: formData.get("to_self") === "true",
      message: formData.get("message"),
      is_metric: metric,
    };
    if (params.to_customer || params.to_self) {
      dispatch({ type: "share-session", payload: { params, event } });
      return onHide();
    }
    console.log("invalid");
  };
  const completeSession = () =>
    dispatch({
      type: "complete-session",
      payload: {
        event: { ...event, session },
        callback: onHide,
      },
    });

  const cancelSession = (params?: { reason: string; notes: string }) =>
    params
      ? dispatch({
          type: "cancel-session",
          payload: {
            event: {
              ...event,
              session: {
                ...session,
                state_reason: params.reason,
                state_notes: params.notes,
              },
            },
          },
        })
      : dispatch({
          type: "cancel-session",
          payload: { event: { ...event, session }, callback: onHide },
        });

  const renderPopupActions = (close: () => void) => {
    if (!session)
      return (
        <ul style={{ marginBottom: 0 }}>
          <li
            id="share"
            onClick={(e) => {
              close();
              return setCurrentViewFromTargetId(e);
            }}
          >
            <Share />
            {t("Share session")}
          </li>
        </ul>
      );
    const incompleteSessionInPast =
      session &&
      event &&
      session.state !== "COMPLETED" &&
      session.state !== "CANCELLED" &&
      hasHappenedBeforeThisInstant(event.start);
    return (
      <ul style={{ marginBottom: 0 }}>
        {incompleteSessionInPast && (
          <li onClick={completeSession}>
            <Done />
            {t("Complete Session")}
          </li>
        )}
        <li
          id="share"
          onClick={(e) => {
            close();
            return setCurrentViewFromTargetId(e);
          }}
        >
          <Share />
          {t("Share session")}
        </li>
        {session.state !== "COMPLETED" && session.state !== "CANCELLED" && (
          <li id="cancel" onClick={setCurrentViewFromTargetId}>
            <Clear />
            {t("Cancel session")}
          </li>
        )}
      </ul>
    );
  };

  const renderSession = () => {
    switch (currentView) {
      case "default":
        return event && session && time ? (
          <SessionDetails
            // eventId={event.id}
            session={session}
            time={time}
            setCurrentViewFromTargetId={setCurrentViewFromTargetId}
            updateWorkout={updateWorkout}
            completeSession={completeSession}
            cancelSession={cancelSession}
            onSubmit={onSubmit}
          />
        ) : event && time ? (
          <Container>
            <SessionAdd event={event} time={time} onSubmit={onSubmit} />
          </Container>
        ) : (
          <></>
        );
      case "reschedule":
        if (
          event &&
          event.session &&
          (event.session.state === "COMPLETED" || event.session.state === "CANCELLED")
        )
          setTimeout(() => setCurrentView(SessionFormView.DEFAULT), 3000);
        return event &&
          event.session &&
          event.session.state !== "COMPLETED" &&
          event.session.state !== "CANCELLED" ? (
          <SessionAvailability
            event={event}
            session={session}
            resetView={resetView}
            dispatch={dispatch}
          />
        ) : (
          <div>
            <span style={{ textTransform: "none" }}>
              {event &&
                event.session &&
                `${t(`Cannot reschedule a ${event.session.state.toLowerCase()} sessions`)}`}
            </span>{" "}
          </div>
        );
      case "scratch":
        return <AddWorkoutWithRouter session-workout={true} handle-submit={createWorkout} />;
      case "template":
        return (
          <WorkoutsListings
            hasSession={true}
            /* TODO: what was this doing hasSelector={true} */
            handleSubmit={createWorkout}
            /* TODO remove/rename. Use something else than noop? */
            setShowWorkout={noop}
            setShowAddWorkout={noop}
          />
        );
      case "edit-workout":
        return event && session && handleWorkout ? (
          <SessionEdit
            event={{ ...event, session }}
            handleWorkout={handleWorkout}
            resetView={resetView}
          />
        ) : (
          event && handleWorkout && (
            <SessionEdit event={event} handleWorkout={handleWorkout} resetView={resetView} />
          )
        );
      case "share":
        return (
          event &&
          event.session && <SessionShare clients={event.session.customer} onSubmit={shareSession} />
        );
      case "cancel":
        return (
          event && event.session && <SessionCancel event={event} cancelSession={cancelSession} />
        );
      default:
        break;
    }
  };

  if (!show) return <></>;

  const actionTitle = getActionTitle(currentView, t, !!session);

  return window.innerWidth < 700 ? (
    <MobileView
      {...{
        actionTitle,
        setCurrentViewFromTargetId,
        handleAdd,
        show,
        onHide,
        classes,
        currentView,
        setCurrentView,
        session,
        renderPopupActions,
        renderSession,
      }}
    />
  ) : (
    <DesktopView
      {...{
        classes,
        onHide,
        show,
        handleAdd,
        setCurrentViewFromTargetId,
        currentView,
        setCurrentView,
        session,
        actionTitle,
        renderPopupActions,
        renderSession,
      }}
    />
  );
}
